import type { RatingQuestion } from '@shared/domain/form';
import type { CurrentReview } from '@shared/domain/review';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Rating } from '@mui/material';
import { motion } from 'framer-motion';
import React from 'react';
import CustomTooltip from '@shared/components/Tooltip';

interface ReviewRatingQuestionProps {
  handleRatingChange: (name: string, value: number | null) => void;
  formFields: any;
  question: RatingQuestion;
  errors: Record<string, string>;
  currentReview?: CurrentReview;
  isReadOnly?: boolean;
  disabled?: boolean;
  isConditional?: boolean;
}

// Gentle settle, no scale pop — SCSS glow ring carries the attention.
const CONDITIONAL_ENTRANCE = {
  initial: { opacity: 0, y: -6 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] as const },
};

const DEFAULT_ENTRANCE = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.25, ease: 'easeOut' as const },
};

function ReviewRatingQuestion(props: ReviewRatingQuestionProps) {
  const {
    handleRatingChange,
    formFields,
    currentReview,
    question,
    errors,
    isReadOnly,
    disabled,
    isConditional = false,
  } = props;

  const onRatingChange = (
    _event: React.SyntheticEvent,
    newValue: number | null,
  ) => {
    handleRatingChange(question.name, newValue);
  };

  const value
    = formFields[question.name]
      || currentReview?.ratings?.find(
        responseRating => responseRating?.type === question.responseName,
      )?.rating
      || 0;
  const hasError = !!(errors && !value && errors[question.name]);

  const motionProps = isConditional ? CONDITIONAL_ENTRANCE : DEFAULT_ENTRANCE;
  const className = [
    'review-question',
    'review-rating',
    isConditional && 'review-question--conditional',
    hasError && 'review-question--has-error',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <motion.section
      className={className}
      data-testid={question.name}
      {...motionProps}
    >
      <h3 id={`${question.name}-label`} className="review-question__prompt">
        <span>{question.question}</span>
        {question.tooltipText && (
          <CustomTooltip title={question.tooltipText} />
        )}
      </h3>

      <div className="review-rating__stars">
        <Rating
          data-testid={`rating-${question.name}-input`}
          onChange={onRatingChange}
          name={question.name}
          size="large"
          value={Number(value)}
          readOnly={isReadOnly}
          disabled={disabled}
          aria-labelledby={`${question.name}-label`}
        />
      </div>

      <span className="review-rating__score" data-testid={`rating-${question.name}-value`}>
        {Number(value) > 0
          ? <><strong>{Number(value)}</strong> of 5</>
          : 'Rate from 1 (low) to 5 (high)'}
      </span>

      {hasError && (
        <div className="review-error" role="alert">
          <WarningAmberIcon />
          <span>{errors[question.name]}</span>
        </div>
      )}
    </motion.section>
  );
}

export default ReviewRatingQuestion;
